import { useQuery } from "@apollo/client/react";
import { GET_ATLAS_RELEASE_STATUS } from "@/graphql/queries";

type Run = { outcome: string; scope: string; sha: string; runUrl: string; reportUrl?: string | null; receivedAt: string };
type Sku = {
  id: string; name: string; branch: string; branchSha?: string | null;
  headState: string; headCheckedAt?: string | null; hilAvailable: boolean;
  releaseTag?: string | null; releaseSha?: string | null; releaseUrl?: string | null;
  build?: Run | null; unit?: Run | null; hil?: Run | null;
};

function Evidence({ label, run, state }: { label: string; run?: Run | null; state: string }) {
  const outcome = state !== "verified" ? "unverified" : run?.outcome || "pending";
  return (
    <div className="text-xs text-text2">
      {label}: {run && state === "verified" ? (
        <a className={outcome === "passed" ? "text-green" : outcome === "failed" ? "text-red" : "text-yellow"} href={run.reportUrl || run.runUrl} target="_blank" rel="noopener noreferrer">
          {outcome}{run.scope === "shared" ? " (shared)" : ""}
        </a>
      ) : <span className="text-yellow">{outcome}</span>}
    </div>
  );
}

export function AtlasReleasePanel({ projectId }: { projectId: string }) {
  const { data, loading, error } = useQuery<{ atlasReleaseStatus: Sku[] }>(GET_ATLAS_RELEASE_STATUS, {
    variables: { projectId }, skip: !projectId, fetchPolicy: "network-only",
  });
  const skus = data?.atlasReleaseStatus || [];
  return (
    <section className="mb-7" data-testid="atlas-release-panel" aria-label="Release and CI">
      <h3 className="text-[13px] uppercase text-text2 tracking-wide mb-3">Release &amp; CI</h3>
      {loading && <p className="text-xs text-text2">Loading release evidence...</p>}
      {error && <p className="text-xs text-red" role="alert">Release evidence unavailable</p>}
      {!loading && !error && skus.length === 0 && <p className="text-xs text-text2">No release SKUs configured.</p>}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-3">
        {skus.map(sku => (
          <div key={sku.id} className="border border-border rounded-default p-4" data-testid={`atlas-sku-${sku.id}`}>
            <h4 className="text-sm text-text mt-0 mb-2">{sku.name}</h4>
            <p className="text-xs text-text2">Latest release: {sku.releaseUrl && sku.releaseTag ? (
              <a href={sku.releaseUrl} target="_blank" rel="noopener noreferrer" className="text-accent">{sku.releaseTag}{sku.releaseTag.includes("-vargus-test-") ? " (test release)" : ""}</a>
            ) : "none"}{sku.releaseSha && <span> · {sku.releaseSha.slice(0, 8)}</span>}</p>
            <p className="text-xs text-text2">Branch: <a href={`https://github.com/fisherpaykeltech/atlas/tree/${sku.branch}`} className="text-accent" target="_blank" rel="noopener noreferrer">{sku.branch}</a> · {sku.headState === "verified" && sku.branchSha ? (
              <a href={`https://github.com/fisherpaykeltech/atlas/commit/${sku.branchSha}`} className="text-accent" target="_blank" rel="noopener noreferrer">{sku.branchSha.slice(0, 8)}</a>
            ) : sku.headState}</p>
            {sku.headCheckedAt && <p className="text-[11px] text-text2">Head checked: {new Date(sku.headCheckedAt).toLocaleString()}</p>}
            <p className="text-[11px] text-text2">Branch CI at verified head{sku.releaseSha && sku.branchSha && sku.releaseSha !== sku.branchSha ? " · published tag uses a different SHA" : ""}</p>
            <Evidence label="Build" run={sku.build} state={sku.headState} />
            <Evidence label="Unit" run={sku.unit} state={sku.headState} />
            {sku.hilAvailable ? <Evidence label="HIL" run={sku.hil} state={sku.headState} />
              : <div className="text-xs text-text2">HIL: unavailable</div>}
          </div>
        ))}
      </div>
    </section>
  );
}
